/** Wer eine Änderung auslöst (AD-3). */
export const actorTypes = ['user', 'mcp', 'rule', 'ai', 'system', 'import'] as const;
export type ActorType = (typeof actorTypes)[number];

/**
 * Rechte-Rollen (AD-20): `user`, `admin`, `packplatz` (Ticket 1.95: nur das Versandzentrum, ohne Preise, Umsätze
 * und Rechnungen; Server-Erlaubnisliste je Route und Kennzeichen `packingStation` je Command; seit Story 22.11 auch die
 * eigene Arbeitszeit) und `mitarbeiter` (Story 22.11: nur Zeiterfassung – Stempeln, eigene Zeiten, eigener Urlaub).
 */
export const roles = ['user', 'admin', 'packplatz', 'mitarbeiter'] as const;
export type Role = (typeof roles)[number];
/** Rollen des Büros (alles Fachliche): Freigeber von Änderungspaketen, Planer von Sammeländerungen, Frag TILL. */
export type OfficeRole = Exclude<Role, 'packplatz' | 'mitarbeiter'>;

/**
 * Rollen, die ein Änderungspaket freigeben, planen oder rückgängig machen dürfen (Spalten `change_set.*_role`): die
 * Büro-Rollen und seit Story 23.17 `mitarbeiter` – nur für die Massenbearbeitung der Liste „Aufgaben“ (prüft
 * `reporting.startMassEdit`). `packplatz` ist nie Freigeber.
 */
export type ApproverRole = Exclude<Role, 'packplatz'>;

/** Darf diese Rolle ein Änderungspaket freigeben (`ApproverRole`)? `packplatz` und „keine Rolle“ nicht. */
export function isApproverRole(role: Role | null | undefined): role is ApproverRole {
  return role === 'user' || role === 'admin' || role === 'mitarbeiter';
}

/** Rolle des Büros (Story 22.11)? `packplatz`, `mitarbeiter` und „keine Rolle“ nicht. */
export function isOfficeRole(role: Role | null | undefined): role is OfficeRole {
  return role === 'user' || role === 'admin';
}

/** Satz, wenn die Rolle `mitarbeiter` etwas außerhalb der Zeiterfassung aufruft (Server 403, Commands, MCP; Story 22.11). */
export const STAFF_ONLY_REJECTED_TEXT = 'Dafür haben Sie keine Berechtigung.';

/** Ablehnungs-Satz einer eingeschränkten Rolle (`packplatz`, `mitarbeiter`); `null` bei Büro-Rollen. */
export function restrictedRoleText(role: Role | null | undefined): string | null {
  if (role === 'packplatz') return PACKING_STATION_REJECTED_TEXT;
  if (role === 'mitarbeiter') return STAFF_ONLY_REJECTED_TEXT;
  return null;
}

/** Satz, wenn die Rolle `packplatz` etwas außerhalb des Versands aufruft (Server 403, Commands, MCP). */
export const PACKING_STATION_REJECTED_TEXT = 'Dein Zugang ist nur für den Versand freigegeben.';

/** Rolle des Packplatzes (Ticket 1.95)? */
export function isPackingStation(role: Role | null | undefined): boolean {
  return role === 'packplatz';
}

/** Actor-Typen, die Admin-Commands nie ausführen dürfen (AD-20). */
export const actorTypesNeverAdmin: readonly ActorType[] = ['mcp', 'ai', 'rule'];

export interface Actor {
  type: ActorType;
  /** ID des Benutzers bzw. der Regel/des Schlüssels; bei `system` leer. */
  id: string | null;
  /** Benutzer, in dessen Auftrag gehandelt wird (z. B. bei `mcp`). */
  onBehalfOf?: string | null;
}

export const actorTypeLabel: Record<ActorType, string> = {
  user: 'Benutzer',
  mcp: 'KI-Assistent (MCP)',
  rule: 'Regel',
  ai: 'KI',
  system: 'System',
  import: 'Import',
};

export const systemActor: Actor = { type: 'system', id: null };

/**
 * Rolle `ai_agent` (AD-36): Ein Hintergrund-Agent handelt als Actor `ai` **ohne** Personen-Rolle (`role: null`). Er
 * darf nur Lese-Werkzeuge nutzen und Commands mit Werkzeug-Markierung (`aiTool`) aufrufen, also Vorschläge und
 * Entwürfe anlegen; alles andere lehnt `commandRejection` ab. Handelt die KI im Auftrag einer angemeldeten Person
 * („KI-Copilot“, später), trägt sie deren Rolle.
 */
export const AI_AGENT_ROLE = 'ai_agent';

export function isAiAgent(ctx: { actor: Actor; role: Role | null }): boolean {
  return ctx.actor.type === 'ai' && ctx.role === null;
}

/**
 * Actor eines Agenten-Laufs: Kennung = ID des Laufs (`ai.agent_run`, UUID wie jede Actor-Kennung im Protokoll); der
 * Lauf nennt Agent und Version. Nie eine Person.
 */
export function aiAgentActor(runId: string, onBehalfOf: string | null = null): Actor {
  return { type: 'ai', id: runId, onBehalfOf };
}
